import { useMemo } from "react"
import { useLocation } from "react-router-dom"
import {
  BreadcrumbNavigationWithMe,
  BreadcrumbPage,
  BreadcrumbDividerIcon,
} from "source/shared/components"
import { Menu, MenuButton, MenuLink, MenuList } from "@reach/menu-button"
import { WideLayout } from "source/Layout"
import { Routes, Route, Navigate, Link } from "react-router-dom"
import { ProfileNavItems } from "./types"
import Text2Give from "./text2give"
import Notifications from "./notifications"
import Statements from "./statements"
import DonationHistory from "./donation_history"
import PlannedGiving from "./planned_giving"
import PaymentMethods from "./payment_methods"
import Overview from "./overview"
import {
  GivingPersonProvider,
  useGivingPersonContext,
} from "./hooks/useGivingPersonContext"
import { css } from "@emotion/react"

export function Profile() {
  return (
    <GivingPersonProvider>
      <ProfileNavigation />
    </GivingPersonProvider>
  )
}

function ProfileNavigation() {
  const { givingPerson } = useGivingPersonContext()

  const location = useLocation()
  const currentPath = location.pathname

  const defaultProfileNavItem = ProfileNavItems[0]

  const personProfileNavItems = ProfileNavItems.filter(
    (item) =>
      item.label !== "Text2Give" ||
      (item.label === "Text2Give" && givingPerson?.can_view_text2give_settings),
  )

  function RouteRequirement({
    requirement,
    redirectToIfRequirementNotMet,
    children,
  }: {
    requirement: boolean
    redirectToIfRequirementNotMet: string
    children: React.ReactNode
  }) {
    return requirement ? (
      children
    ) : (
      <Navigate to={redirectToIfRequirementNotMet} replace />
    )
  }

  const activeNavItem = useMemo(() => {
    return (
      ProfileNavItems.find((item) => item.path === currentPath) ??
      defaultProfileNavItem
    )
  }, [currentPath, defaultProfileNavItem])

  return (
    <WideLayout>
      <BreadcrumbNavigationWithMe>
        {activeNavItem.label !== "Overview" && (
          <>
            <BreadcrumbPage linkUrl="/giving/profile" pageText="My giving" />
            <BreadcrumbDividerIcon />
          </>
        )}
        <div className="d-n d-b@md breadcrumbs__page">
          <BreadcrumbPage pageText={activeNavItem.label} isActive />
        </div>
        <div className="d-n@md">
          <Menu>
            <MenuButton
              className="select fw-600"
              css={css`
                border: none;
              `}
            >
              {activeNavItem.label}
            </MenuButton>
            <MenuList>
              {personProfileNavItems.map((item, index) => (
                <MenuLink as={Link} to={item.path} key={index}>
                  {item.label}
                </MenuLink>
              ))}
            </MenuList>
          </Menu>
        </div>
      </BreadcrumbNavigationWithMe>

      <div className="d-f@md">
        <nav className="sub-navigation mr-4 d-n d-b@md">
          {personProfileNavItems.map((item, index) => (
            <Link
              className={`sub-navigation__link ${
                activeNavItem.path === item.path ? "is-active-link" : null
              }`}
              to={item.path}
              key={index}
              tabIndex={0}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="f-1">
          <Routes>
            <Route path="/" element={<Overview />} />
            <Route path="/donations" element={<DonationHistory />} />
            <Route
              path="/recurring-donations"
              element={<Navigate to="/planned" replace />}
            />
            <Route
              path="/pledges"
              element={<Navigate to="/planned" replace />}
            />
            <Route path="/planned" element={<PlannedGiving />} />
            <Route path="/payment-methods" element={<PaymentMethods />} />
            <Route
              path="/text-to-give"
              element={
                <RouteRequirement
                  requirement={givingPerson?.can_view_text2give_settings}
                  redirectToIfRequirementNotMet="/giving/profile"
                >
                  <Text2Give />
                </RouteRequirement>
              }
            />
            <Route path="/notifications" element={<Notifications />} />
            <Route path="/statements" element={<Statements />} />
          </Routes>
        </div>
      </div>
    </WideLayout>
  )
}
